import { RoomName } from '@/types/types';
import { ApiManager, GameOrganizer } from '@/utils';
import EventModifier from '@/utils/EventModifier';
import { resolveAccountSlugFromHostname } from '@infrastructure/routing/knownAccountDomains';
import { TemplateResult, html } from "lit";
import UniversalRouter, { RouteParams } from 'universal-router';
import { getStringParam } from './routerUtils';

export interface AppRouterDependencies {
	eventDataSource: ApiManager;
	gameOrganizer: GameOrganizer;
	eventModifier: EventModifier;
}

export function notFoundPage(): TemplateResult {
	return html`<not-found-page></not-found-page>`;
}

export function isKnownRoomName(roomId: string): boolean {
	return Object.values(RoomName).includes(roomId as RoomName);
}

export function roomRouteWithDomainResolvedAccount({ params }: { params: RouteParams }): TemplateResult {
	const roomId = getStringParam(params.roomId);
	if (!isKnownRoomName(roomId)) {
		return notFoundPage();
	}
	if (!resolveAccountSlugFromHostname(window.location.hostname)) {
		return notFoundPage();
	}
	return html`<room-page .roomId="${roomId}"></room-page>`;
}

export function explicitAccountAndLocationRoomRoute({ params }: { params: RouteParams }): TemplateResult {
	const roomId = getStringParam(params.roomId);
	if (!isKnownRoomName(roomId)) {
		return notFoundPage();
	}
	return html`<room-page .roomId="${roomId}"></room-page>`;
}

export function createAppRouter(dependencies: AppRouterDependencies): UniversalRouter {
	const { eventDataSource, gameOrganizer, eventModifier } = dependencies;

	return new UniversalRouter([
		{ path: '/room/:roomId', action: roomRouteWithDomainResolvedAccount },
		{ path: '/:locationSlug/room/:roomId', action: roomRouteWithDomainResolvedAccount },
		{ path: '/:accountSlug/:locationSlug/room/:roomId', action: explicitAccountAndLocationRoomRoute },
		{
			path: '/foyer',
			action: () => html`<video-player videoLink="https://www.youtube.com/embed/WIqSuaurx5Q?si=Rvq-T4tJz917E5Zu&autoplay=1&loop=1&playlist=WIqSuaurx5Q&mute=1"></video-player>`,
		},
		{ path: '/buzzer', action: () => html`<team-buzzer></team-buzzer>` },

		// Protected host routes
		{
			path: '/host',
			// middleware-like action
			async action(context) {
				const res = await fetch("/api/auth/whoami/", { credentials: "include" });
				const data = await res.json();
				if (!data.authenticated) {
					return { redirect: "/login", from: context.pathname };
				}
				// authenticated → continue into children
				return context.next();
			},
			children: [
				{
					path: '/events',
					action: () => ({
						content: html`<events-page
						.eventsDataSource="${eventDataSource}"
					></events-page>`,
					}),
				},
				{
					path: '/create-event',
					action: () => ({
						content: html`<create-event-page
						.eventDataSource="${eventDataSource}"
						.eventModifier="${eventModifier}"
					></create-event-page>`,
					}),
				},
				{
					path: '/update-event/:eventId',
					action: ({ params }) => {
						const eventId = getStringParam(params.eventId);
						return {
							content: html`<create-event-page
							.eventToBeUpdatedID="${eventId}"
							.eventDataSource="${eventDataSource}"
							.eventModifier="${eventModifier}"
							></create-event-page>`,
						};
					},
				},
				{
					path: '/moderate-event/:eventId',
					action: ({ params }) => {
						const eventId = getStringParam(params.eventId);
						return {
							content: html`<moderate-event-page
							.gameOrganizer="${gameOrganizer}"
							.eventId="${eventId}"
							.eventDataSource="${eventDataSource}"
							></moderate-event-page>`,
						};
					},
				},
				{
					path: '/moderate-event/:eventId/:gameId',
					action: ({ params }) => {
						const eventId = getStringParam(params.eventId);
						const gameId = getStringParam(params.gameId);
						return {
							content: html`<moderate-game-page
							.eventId="${eventId}"
							.gameId="${gameId}"
							.eventDataSource="${eventDataSource}"
							></moderate-game-page>`,
						};
					},
				},
				{
					path: '/buzzer',
					action: () => ({
						content: html`<buzzer-moderator></buzzer-moderator>`,
					}),
				},
			],
		},

		// Root redirect
		{
			path: '/',
			async action() {
				const res = await fetch("/api/auth/whoami/", { credentials: "include" });
				const data = await res.json();
				if (data.authenticated) {
					return { redirect: "/host/events" };
				}
				return { redirect: "/login" };
			},
		},

		// Login
		{ path: '/login', action: () => ({ content: html`<login-page></login-page>` }) },

		// Quiz routes
		{ path: '/quiz-start', action: () => ({ content: html`<quiz-start-page></quiz-start-page>` }) },
		{ path: '/quiz', action: () => ({ content: html`<quiz-page></quiz-page>` }) },
		{ path: '/questions', action: () => ({ content: html`<questions-page></questions-page>` }) },
		{ path: '/millionaire-questions', action: () => ({ content: html`<millionaire-questions-page></millionaire-questions-page>` }) },
		{ path: '/quizzes', action: () => ({ content: html`<my-quizzes-page></my-quizzes-page>` }) },
		{ path: '/quiz/create/:id', action: (ctx: any) => ({ content: html`<quiz-create-page quizId=${ctx.params.id}></quiz-create-page>` }) },

		// Fallback
		{ path: '*all', action: () => ({ content: notFoundPage() }) },
	]);
}
